import { useState, useRef, useEffect } from 'react'
import { ListFilter } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { FilterField, FilterOperator, AppliedFilters } from './filterTypes'
import { FilterChip } from './FilterChip'
import { FilterFieldSelector } from './FilterFieldSelector'
import { FilterOperatorSelector } from './FilterOperatorSelector'
import { FilterValueInput } from './FilterValueInput'
import { useFilters } from '@features/file-explorer/hooks/useFilters'

interface AdvancedFilterBarProps {
  filterFields: FilterField[]
  initialFilters?: AppliedFilters
  onApplyFilters: (filters: AppliedFilters) => void
  isLoading?: boolean
}

type FilterStep = 'field' | 'operator' | 'value'

export function AdvancedFilterBar({
  filterFields,
  initialFilters = {},
  onApplyFilters,
  isLoading = false
}: AdvancedFilterBarProps) {
  const { t } = useTranslation()
  const {
    activeFilters,
    inputValue,
    setInputValue,
    addFilter,
    removeFilter
  } = useFilters({
    initialFilters,
    filterFields,
    onApplyFilters
  })

  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [currentStep, setCurrentStep] = useState<FilterStep>('field')
  const [selectedField, setSelectedField] = useState<FilterField | null>(null)
  const [selectedOperator, setSelectedOperator] = useState<FilterOperator | null>(null)

  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false)
        resetDropdown()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const resetDropdown = () => {
    setCurrentStep('field')
    setSelectedField(null)
    setSelectedOperator(null)
    setInputValue('')
  }

  const handleFieldSelect = (field: FilterField) => {
    setSelectedField(field)

    // Skip operator step for type filter and go directly to value
    if (field.key === 'type') {
      setSelectedOperator('equals')
      setCurrentStep('value')
    } else {
      setCurrentStep('operator')
    }
  }

  const handleOperatorSelect = (operator: FilterOperator) => {
    setSelectedOperator(operator)
    setCurrentStep('value')
  }

  const handleValueSubmit = (value: string, valueLabel: string) => {
    if (!selectedField || !selectedOperator) return

    addFilter({
      field: selectedField.key,
      operator: selectedOperator,
      value,
      fieldLabel: selectedField.label,
      operatorLabel: t(`fileExplorer.filters.operators.${selectedOperator}`),
      valueLabel
    })

    resetDropdown()
    setDropdownOpen(false)

    setTimeout(() => {
      inputRef.current?.focus()
      setDropdownOpen(true)
    }, 100)
  }

  const handleQuickFilter = () => {
    const nameField = filterFields.find(f => f.key === 'name')
    if (nameField) {
      addFilter({
        field: 'name',
        operator: 'contains',
        value: inputValue.trim(),
        fieldLabel: nameField.label,
        operatorLabel: t('fileExplorer.filters.operators.contains'),
        valueLabel: inputValue.trim()
      })
      resetDropdown()
      setDropdownOpen(false)
      setTimeout(() => {
        inputRef.current?.focus()
        setDropdownOpen(true)
      }, 100)
    }
  }

  const handleInputFocus = () => {
    setDropdownOpen(true)
  }

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (currentStep === 'field' && inputValue.trim()) {
        handleQuickFilter()
      }
    } else if (e.key === 'Escape') {
      setDropdownOpen(false)
      resetDropdown()
    }
  }

  return (
    <div className="relative flex items-center w-full border border-base-300 rounded-lg overflow-visible bg-base-100">
      {/* Icon */}
      <div className="flex items-center px-3 py-2 border-r border-base-300">
        <ListFilter className="w-4 h-4 text-base-content/50" />
      </div>

      {/* Active Filters */}
      <div className="flex items-center gap-2 overflow-x-auto px-2 max-w-md scrollbar-hide">
        {activeFilters.map((filter) => (
          <div key={filter.id} className="flex-shrink-0">
            <FilterChip filter={filter} onRemove={removeFilter} />
          </div>
        ))}
      </div>

      {/* Input */}
      <div className="relative flex-grow">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onFocus={handleInputFocus}
          onKeyDown={handleInputKeyDown}
          placeholder={t('fileExplorer.filters.placeholder')}
          className="w-full px-3 py-2 outline-none focus:outline-none bg-transparent border-none focus-none"
          autoComplete="off"
          disabled={isLoading}
          data-testid="filter-input"
        />

        {/* Dropdown */}
        {dropdownOpen && !isLoading && (
          <div
            ref={dropdownRef}
            className="absolute top-full left-0 mt-2 bg-base-100 border border-base-300 rounded-lg shadow-lg py-2 min-w-48 max-h-96 overflow-y-auto z-50"
          >
            {currentStep === 'field' && (
              <FilterFieldSelector
                filterFields={filterFields}
                inputValue={inputValue}
                onFieldSelect={handleFieldSelect}
                onQuickFilter={inputValue.trim() ? handleQuickFilter : undefined}
              />
            )}

            {currentStep === 'operator' && selectedField && (
              <FilterOperatorSelector
                field={selectedField}
                onOperatorSelect={handleOperatorSelect}
              />
            )}

            {currentStep === 'value' && selectedField && selectedOperator && (
              <FilterValueInput
                field={selectedField}
                operator={selectedOperator}
                onSubmit={handleValueSubmit}
              />
            )}
          </div>
        )}
      </div>
    </div>
  )
}
